import React, { useEffect } from "react";
import Card from "./Card";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import data from "../../data/index.json";
import { useTranslation } from "react-i18next";
import { HiArrowRight, HiExternalLink } from "react-icons/hi";
import { FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { getBlogImage } from "../../constants/images";
import { Section, SectionHeader, reveal } from "../../components";

const SOCIALS = [
  {
    name: "Facebook",
    handle: "@HyphenTech",
    // Vanity-free profile URL — this is the page's canonical id.
    href: "https://www.facebook.com/profile.php?id=100064389290696",
    Icon: FaFacebookF,
  },
  {
    name: "LinkedIn",
    handle: "Hyphen Tech",
    href: "https://www.linkedin.com/company/hyphen-tech-1",
    Icon: FaLinkedinIn,
  },
];

const Blog = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const blogPosts = data.BlogApi || [];
  const [lead, ...rest] = blogPosts;

  useEffect(() => {
    if (location.state?.scrollPosition) {
      window.scrollTo(0, location.state.scrollPosition);
    }
  }, [location.state]);

  return (
    <Section id="blog" tone="warm">
      <SectionHeader
        index="09"
        label={t("Knowledge Base")}
        title={
          <>
            {t("The Hyphen")} <em>{t("Chronicles")}</em>
          </>
        }
        lede={t(
          "Deep dives into the intersection of technology, culture, and progress."
        )}
        aside={
          <Link to="/blog" className="link-underline text-sm font-medium">
            {t("All articles")}
            <HiArrowRight aria-hidden />
          </Link>
        }
      />

      {/* ── Articles ── */}
      <div className="mt-14 md:mt-20">
        {lead && (
          <motion.article
            {...reveal()}
            className="group relative border-y border-[color:var(--rule)] pt-10 pb-12 mb-12"
          >
            <Link
              to={`/blog/${lead.id}`}
              state={{ scrollPosition: window.scrollY }}
              className="absolute inset-0 z-10"
              aria-label={t(lead.title_one)}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div className="overflow-hidden bg-paper-tint aspect-[16/10]">
                <img
                  src={getBlogImage(lead.image)}
                  alt={t(lead.title_one)}
                  loading="lazy"
                  className="img-editorial"
                />
              </div>
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="label tabular">{lead.date}</span>
                  <span className="label text-gold-ink">{t("Latest")}</span>
                </div>
                <h3 className="display-2 mt-4">{t(lead.title_one)}</h3>
                <p className="prose-body mt-4 text-[0.9375rem]">
                  {t(lead.desc_one)}
                </p>
                <span className="link-underline text-sm font-medium mt-6 inline-flex">
                  {t("Read Article")}
                  <HiArrowRight aria-hidden className="text-sm" />
                </span>
              </div>
            </div>
          </motion.article>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {rest.map((blog, index) => (
            <motion.div key={blog.id} {...reveal(index)} className="group relative">
              <Link
                to={`/blog/${blog.id}`}
                state={{ scrollPosition: window.scrollY }}
                className="absolute inset-0 z-10"
                aria-label={t(blog.title_one)}
              />
              <Card blog={blog} />
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Follow ──
          The old Facebook Page Plugin iframe was replaced: it renders an empty
          box whenever a tracker blocker or missing consent kills the embed, it
          ships Meta's script to every visitor, and it can't be styled. A plain
          outbound card is reliable, private, and on-system. */}
      <motion.div
        {...reveal()}
        className="mt-16 md:mt-24 pt-3 border-t border-[color:var(--rule)]"
      >
        <span className="label">{t("Follow Our Journey")}</span>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 mt-4">
          {SOCIALS.map(({ name, handle, href, Icon }) => (
            <li key={name}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-5 py-6 border-b border-[color:var(--rule)]"
              >
                <Icon
                  aria-hidden
                  className="text-2xl text-ink-faint transition-colors duration-200 group-hover:text-ink shrink-0"
                />
                <span className="flex-1 min-w-0">
                  <span className="display-3 block">{name}</span>
                  <span className="label block mt-1">{handle}</span>
                </span>
                <HiExternalLink
                  aria-hidden
                  className="text-lg text-ink-faint transition-all duration-200 group-hover:text-ink group-hover:-translate-y-0.5"
                />
              </a>
            </li>
          ))}
        </ul>
      </motion.div>
    </Section>
  );
};

export default Blog;
